import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";

import arrowIcon from "../../../assets/previous_icon.png";
import editIcon from "../../../assets/edit_icon.png";
import closeIcon from "../../../assets/close_icon.png";
import saveIcon from "../../../assets/save_icon.png";
import checkIcon from "../../../assets/check_icon.png";
import printIcon from "../../../assets/printer_icon.png";

import CustomButton from "../../../components/CustomButton";
import AreYouSureModal from "../../../components/AreYouSureModal";
import { useNavigate } from "react-router-dom";
import CustomInputWithUnit from "../../../components/CustomInputWithUnit";
import { useReactToPrint } from "react-to-print";
import { updateProductOrder } from "../../../services/productServices";
import { getAllKassas } from "../../../services/kassa/kassaServices";
import HotdropdownV2 from "../../../components/HotdropdownV2";
import Loader from "../../../components/Loader";
import PrintableProductOrderView from "./PrintableProductOrder";
import { parse } from "date-fns";
import CustomInput from "../../../components/CustomInput";
import CustomCheckbox from "../../../components/CustomCheckbox";
import { useContext } from "react";
import { UserAuth } from "../../../utils/AuthContext";

const ProductOrders = ({
  data,
  setData,
  orderStatus = "PENDING",
  setRefetch,
}) => {
  // HOOKS
  const { t } = useTranslation("general");
  const navigate = useNavigate();

  // STATES
  const [selectedItems, setSelectedItems] = useState({});
  const [collapsed, setCollapsed] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [editableData, setEditableData] = useState(null);
  const [editableNotes, setEditableNotes] = useState();
  const [loading, setLoading] = useState(false);
  const [areYouSureModal, setAreYouSureModal] = useState(false);
  const [kassaLoading, setKassasLoading] = useState(false);
  const [kassas, setKassas] = useState([]);
  const [selectedKassa, setSelectedKassa] = useState(null);
  const [error, setError] = useState(null);
  const printRef = useRef();
  const { userData } = useContext(UserAuth);
  

  // Extract the first order from the data array
  const currentOrder = Array.isArray(data) && data.length > 0 ? data[0] : null;
  const productOrderItems = currentOrder?.ProductOrderItems || [];

  // Group products by product type for better organization
  const groupedProducts = productOrderItems.reduce((acc, item) => {
    const productType = item.product?.productType || "Unknown";
    if (!acc[productType]) {
      acc[productType] = [];
    }
    acc[productType].push(item);
    return acc;
  }, {});

  const productTypesList = Object.entries(
    isEditMode && editableData
      ? editableData.reduce((acc, item) => {
          const productType = item.product?.productType || "Unknown";
          if (!acc[productType]) {
            acc[productType] = [];
          }
          acc[productType].push({...item});
          return acc;
        }, {})
      : groupedProducts
  );

  // fetch kassas
  const fetchKassas = async () => {
    setKassasLoading(true);
    try {
      const response = await getAllKassas({
        page: 1,
        limit: 100,
      });

      if (response.status === 200) {
        setKassas(response.data.kassas);

        // Set first kassa as default if available
        if (response.data.kassas.length > 0) {
          const firstKassa = response.data.kassas[0];
          setSelectedKassa({
            title: firstKassa.name,
            value: firstKassa.id,
            kassaData: firstKassa,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching Kassas:", error);
      setError("failedToFetchKassas");
    } finally {
      setKassasLoading(false);
    }
  };

  const transformKassasForDropdown = (kassasData) => {
    return kassasData.map((kassa) => ({
      title: kassa.name,
      value: kassa.id,
      kassaData: kassa,
    }));
  };

  const handleCheckboxChange = (itemId) => {
    setSelectedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  // Updated handleInputChange function with auto-calculation
  const handleInputChange = (id, field, value) => {
    setEditableData((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const updatedItem = { ...item, [field]: value };

          // Auto-calculate total price when price or quantity changes
          if (field === "price" || field === "quantity") {
            const price =
              field === "price" ? value : updatedItem.price || 0;
            const quantity =
              field === "quantity" ? value : updatedItem.quantity||
              0;
            updatedItem.totalPrice = parseFloat((price * quantity).toFixed(3))
          }

          return updatedItem;
        }
        return item;
      })
    );
  };

  const toggleCollapse = (productType) => {
    setCollapsed((prev) => ({
      ...prev,
      [productType]: !prev[productType],
    }));
  };

  const handleEdit = () => {
    const prechecked = {};
    const deepCopy = productOrderItems.map((item) => {
      prechecked[item.id] = true;
      return {
        ...item,
        quantity: item.quantity || "",
        requestedQuantity: item.requestedQuantity || item.quantity || "",
        price: item.price || 0,
        totalPrice: item.totalPrice || "",
      };
    });

    setSelectedItems(prechecked);
    setEditableData(deepCopy);
    setIsEditMode(true);
    setEditableNotes(currentOrder.notes);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setEditableData(null);
    setSelectedItems({});
    setEditableNotes(currentOrder.notes);
  };

  /**
   * Handles saving the edited order.
   * This action updates the order with a PENDING status.
   */
  const handleSave = async () => {
    if (!editableData || !currentOrder) return;

    setLoading(true);
    try {
      const updatedItems = editableData.map((item) => ({
        kassaId: selectedKassa?.value || currentOrder.kassaId,
        productId: item.productId,
        price: parseFloat(item.price || 0),
        quantity: parseInt(item.quantity),
        requestedQuantity: parseInt(item.requestedQuantity || item.quantity),
        totalPrice: parseFloat(item.totalPrice) || 0,
      }));

      const updateData = {
        orderNumber: currentOrder.orderNumber,
        orders: updatedItems,
        notes: editableNotes || currentOrder.notes,
        status: "PENDING",
      };

      const response = await updateProductOrder(updateData);

      if (response.status === 200) {
        const newOrder = response.data;
        setRefetch((prev) => !prev);
        setIsEditMode(false);
        setEditableData(null);
        setSelectedItems({});
      }
    } catch (error) {
      console.error("Error saving order edits:", error);
      setError("failedToSaveOrder");
    } finally {
      setLoading(false);
    }
  };

  // Validation function to check if order can be confirmed
  const canConfirmOrder = () => {
    return productOrderItems.every((item) => {
      const hasQuantity = item.quantity && parseInt(item.quantity) > 0;
      const hasTotalPrice = item.totalPrice && parseFloat(item.totalPrice) > 0;
      return hasQuantity && hasTotalPrice;
    });
  };

  /**
   * Handles confirming the order.
   * This action updates the order with a CONFIRMED status.
   */
  const handleConfirmOrder = async () => {
    if (!currentOrder) return;

    setLoading(true);
    try {
      const updateData = {
        orderNumber: currentOrder.orderNumber,
        kassaId: selectedKassa?.value || currentOrder.kassaId, // Move here
        status: "CONFIRMED",
        notes: currentOrder.notes,
        date: currentOrder.date,
        orders: productOrderItems.map((item) => ({
          productId: item.productId,
          price: parseFloat(item.price || item.product?.price || 0),
          quantity: parseInt(item.quantity),
          requestedQuantity: parseInt(item.requestedQuantity || item.quantity),
          totalPrice: parseFloat(item.totalPrice) || 0, // Use totalPrice from response
        })),
      };

      const response = await updateProductOrder(updateData);

      if (response.status === 200) {
        setAreYouSureModal(false);
        const newOrder = response.data;
        // Correctly update the state to an array containing the new order.
        setData([newOrder]);
        navigate(-1);
      }
    } catch (error) {
      console.error("Error confirming order:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAttemptToConfirmReceived = () => setAreYouSureModal(true);

  const preparePrintData = () => {
    // Group products by product type (category)
    const groupedByType = productOrderItems.reduce((acc, item) => {
      const productType = item.product?.productType || "Unknown";

      if (!acc[productType]) {
        acc[productType] = {
          id: productType,
          category: productType,
          books: [],
        };
      }

      acc[productType].books.push({
        name: item.product?.name,
        productType: item.product?.productType,
        academicLevel: item.product.schoolClass?.name,
        productSubType: t(item.product?.productSubType),
        quantity: item.receivedAmount || item.quantity,
        price: parseFloat(item.price) || parseFloat(item.product?.price) || 0,
        totalPrice: item.totalPrice,
      });

      return acc;
    }, {});

    return Object.values(groupedByType);
  };

  const orderDetails = {
    orderNumber: currentOrder?.orderNumber,
    date: currentOrder?.date,
    companyName: currentOrder.creator?.organization?.organizationName,
    createdBy: currentOrder.creator?.username,
    notes: currentOrder?.notes || "",
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    pageStyle: `
        @page { 
            size: A4; 
            margin: 5mm;
            width: 21cm;
            height: 29.7cm;
        }
        @media print {
            body { 
                -webkit-print-color-adjust: exact; 
                padding: 10mm;
            }
            .printable-order-view { display: flex !important;
            }
        }
        `,
  });

  useEffect(() => {
    if (orderStatus === "PENDING") {
      fetchKassas();
    }
  }, []);

  // Early return if no current order
  if (!currentOrder) {
    return <div>No order data available</div>;
  }

  return (
    <div className="c-fabric-table">
      <div className="o-row o-row--horizontal-align_space-between">
        <h1 className="t-headline-1">
          {t("orderNumber")}: {currentOrder.orderNumber}
        </h1>
      </div>

      {/* Notes Section */}
      <div className="o-row u-margin--top_300 u-margin--bottom_300">
        <div
          className="o-column o-column--gap-size_100"
          style={{ width: "100%" }}
        >
          <label className="t-bold_bold">{t("notes")}:</label>
          {isEditMode ? (
            <CustomInput
              type="textarea"
              value={editableNotes}
              onChange={(e) => setEditableNotes(e.target.value)}
            />
          ) : (
            <div>{currentOrder.notes || t("noNotes")}</div>
          )}
        </div>
      </div>

      <div className="o-row o-row--horizontal-align_space-between o-row--vertical-align_end u-margin--top_300 u-margin--bottom_600">
        {orderStatus === "PENDING" &&
          (kassaLoading ? (
            <Loader size="small" />
          ) : kassas.length > 0 ? (
            <>
              <div className="o-column o-column--gap-size_100">
                <label className="t-bold_bold">{t("selectKassa")}:</label>
                <HotdropdownV2
                  initOptions={transformKassasForDropdown(kassas)}
                  selected={selectedKassa?.title || ""}
                  setSelected={(option) => setSelectedKassa(option)}
                  optionsName="kassas"
                  thick
                />
              </div>
              {orderStatus === "PENDING" && (
                <>
                  {!isEditMode ? (
                    <CustomButton
                      variant="blank"
                      title={t("edit")}
                      onClick={handleEdit}
                      icon={editIcon}
                      iconSize={18}
                    />
                  ) : (
                    <div className="o-row o-row--gap-size_300">
                      <CustomButton
                        title={t("cancel")}
                        onClick={handleCancel}
                        variant="blank"
                        icon={closeIcon}
                        iconSize={14}
                      />
                      <CustomButton
                        title={loading ? t("saving") : t("save")}
                        onClick={handleSave}
                        icon={saveIcon}
                        iconSize={16}
                      />
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <span className="u-color_error">{t("noKassasAvailable")}</span>
          ))}
      </div>
      {productTypesList.map(([productType, items]) => (
        <div key={productType} className="c-fabric-block">
          <div
            className="c-fabric-header"
            onClick={() => toggleCollapse(productType)}
          >
            <span
              className="c-collapse-icon"
              style={{
                cursor: "pointer",
                transform: collapsed[productType]
                  ? "rotate(0deg)"
                  : "rotate(-90deg)",
                transition: "transform 0.3s ease",
              }}
            >
              <img src={arrowIcon} alt="Collapse" style={{ width: 16 }} />
            </span>
            <span className="c-fabric-code">
              {t("productType")}: {t(productType)}
            </span>
          </div>

          {!collapsed[productType] && (
            <table className="c-fabric-inner-table">
              <thead>
                <tr>
                  {isEditMode && <th></th>}
                  <th>{t("productName")}</th>
                  <th>{t("productSubType")}</th>
                  <th>{t("singlePrice")}</th>
                  {orderStatus === "PENDING" && (
                    <th>{t("requestedQuantity")}</th>
                  )}
                  <th>{t("receivedQuantity")}</th>
                  <th>{t("finalAmount")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    {isEditMode && (
                      <td>
                        <CustomCheckbox
                          checked={!!selectedItems[item.id]}
                          onChange={() => handleCheckboxChange(item.id)}
                        />
                      </td>
                    )}
                    <td>{item.product?.name}</td>
                    <td>{t(item.product?.productSubType)}</td>
                    <td>
                      {isEditMode ? (
                        <CustomInputWithUnit
                          type="money"
                          value={item.price || 0}
                          onChange={(e) =>
                            handleInputChange(item.id, "price", e.target.value)
                          }
                          flex
                          unit="IQD"
                        />
                      ) : (
                        `${item.price || 0} IQD`
                      )}
                    </td>

                    {/* REQUESTED QUANTITY */}
                    {orderStatus === "PENDING" && (
                      <td>
                        {isEditMode ? (
                          <CustomInputWithUnit
                            type="text"
                            value={item.requestedQuantity || ""}
                            onChange={(e) =>
                              handleInputChange(
                                item.id,
                                "requestedQuantity",
                                e.target.value
                              )
                            }
                            flex
                            unit={t("pieces")}
                          />
                        ) : (
                          `${item.requestedQuantity} ${t("pieces")}`
                        )}
                      </td>
                    )}

                    {/* RECEIVED QUANTITY */}
                    <td>
                      {isEditMode ? (
                        <CustomInputWithUnit
                          type="money"
                          value={item.quantity || ""}
                          onChange={(e) =>
                            handleInputChange(
                              item.id,
                              "quantity",
                              e.target.value
                            )
                          }
                          flex
                          unit={t("pieces")}
                        />
                      ) : item.quantity ? (
                        `${item.quantity} ${t("pieces")}`
                      ) : (
                        "-"
                      )}
                    </td>

                    {/* FINAL AMOUNT */}
                    <td>
                      {isEditMode ? (
                        <CustomInputWithUnit
                          type="money"
                          value={item.totalPrice !== undefined ? item.totalPrice: ""}
                          onChange={(e) =>
                            handleInputChange(
                              item.id,
                              "totalPrice",
                              e.target.value
                            )
                          }
                          flex
                          unit={"IQD"}
                        />
                      ) : item.totalPrice ? (
                        `${item.totalPrice} ${"IQD"}`
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
      <div className="u-margin--top_500 u-margin--bottom_500 o-row o-row--horizontal-align_end o-row--gap-size_400">
        {!isEditMode && (
          <>
            <CustomButton
              variant="gray"
              title={t("print")}
              onClick={handlePrint}
              icon={printIcon}
              iconSize={18}
            />
            {orderStatus === "PENDING" && (
              <CustomButton
                title={t("orderReceived")}
                variant="green"
                onClick={handleAttemptToConfirmReceived}
                icon={checkIcon}
                iconSize={18}
                disabled={!canConfirmOrder()}
              />
            )}
          </>
        )}

        {areYouSureModal && (
          <AreYouSureModal
            onClose={() => setAreYouSureModal(false)}
            onConfirm={handleConfirmOrder}
            customMessage="areYouSureToConfirmReceivedOrder"
            closeTitle="cancel"
            confirmTitle="confirm"
            loading={loading}
            confirm
            confirmButtonVariant={null}
            confirmIconType="whiteCheck"

          />
        )}
      </div>

      <PrintableProductOrderView
        ref={printRef}
        data={preparePrintData()}
        orderDetails={orderDetails}
        currency={{ value: "IQD" }}
      />
    </div>
  );
};

export default ProductOrders;
